/**
 * Pure state shared by every AGU Research Accelerator finder page (Grant,
 * Collaborator and Job Finder — Tasks 16-18). Kept host- and Angular-free so it
 * can be unit-tested without TestBed and reused verbatim across the three
 * finder page components, which otherwise duplicate the same tab/query/page/
 * filter bookkeeping.
 */

/** The two tabs every finder page shows: search results vs. the member's picks. */
export type FinderTab = 'all' | 'recommended';

/** Default page size when a caller doesn't specify one — mirrors the server's `research.search.defaultCount` default (apps/MJAPI/src/resolvers/research/settings.ts). */
const DEFAULT_PAGE_SIZE = 20;

/** One finder page's full UI state: tab, query text/attachments, filters, sort, and paging. */
export interface FinderPageState<TFilters> {
    tab: FinderTab;
    query: string;
    documentIDs: string[];
    filters: TFilters;
    sort: string;
    page: number;
    pageSize: number;
}

/** A fresh finder page state on the All tab, page 1, with no query or attachments. */
export function InitialFinderState<TFilters>(filters: TFilters, sort: string, pageSize?: number): FinderPageState<TFilters> {
    return {
        tab: 'all',
        query: '',
        documentIDs: [],
        filters,
        sort,
        page: 1,
        pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
    };
}

/**
 * Counts filter fields that are actually constraining the search — the number
 * shown as the toolbar's filter-popover badge. A field counts when it is a
 * non-empty array, `true`, a non-empty string, or a finite number; `undefined`,
 * `null`, `false`, `''` and empty arrays do not.
 */
export function CountActiveFilters(filters: Record<string, unknown>): number {
    let count = 0;
    for (const value of Object.values(filters)) {
        if (Array.isArray(value)) {
            if (value.length > 0) count++;
        } else if (typeof value === 'boolean') {
            if (value) count++;
        } else if (typeof value === 'string') {
            if (value.length > 0) count++;
        } else if (typeof value === 'number') {
            if (Number.isFinite(value)) count++;
        }
    }
    return count;
}

/**
 * Clears every filter field back to its "no constraint" shape: arrays → `[]`,
 * booleans → `false`, everything else (strings, numbers, objects) →
 * `undefined` so the field drops out of the request entirely.
 */
export function ResetFilters<TFilters extends Record<string, unknown>>(filters: TFilters): TFilters {
    const out = {} as TFilters;
    for (const key of Object.keys(filters) as (keyof TFilters)[]) {
        const value = filters[key];
        if (Array.isArray(value)) {
            out[key] = [] as unknown as TFilters[keyof TFilters];
        } else if (typeof value === 'boolean') {
            out[key] = false as unknown as TFilters[keyof TFilters];
        } else {
            out[key] = undefined as unknown as TFilters[keyof TFilters];
        }
    }
    return out;
}

/** Moves to a different page without disturbing tab, query or filters. */
export function WithPage<T>(s: FinderPageState<T>, page: number): FinderPageState<T> {
    return { ...s, page };
}

/**
 * A new search query always lands back on page 1 of the All tab — the tab a query actually searches.
 * Pure state only: a page that mirrors its tab into the URL (`id=recommended`) must re-sync the
 * URL after this, or browser Back re-applies the tab the member left (D-20/G-15).
 */
export function WithQuery<T>(s: FinderPageState<T>, query: string): FinderPageState<T> {
    return { ...s, query, page: 1, tab: 'all' };
}
